import { ApplicationGroup, StartDayOption } from '@amzn/awscat-portfolio-assessment-types';
import { RadioGroupProps, SelectProps, TabsProps } from '@amzn/awsui-components-react';

import { constants } from '../../../common/Localization';
import { formatNumberAsString } from '../../../common/LocalizationHelper';
import { ColumnDefinition, ColumnTypes } from '../../../common/table/types';
import { getCell } from '../../common/awsui-table-helper';

export const TIMELINE_ZOOM_MIN = 5 * 24 * 60 * 60 * 1000; //the min zoom interval is set to 5 days in milliseconds
export const TIMELINE_ZOOM_MAX = 5 * 365 * 24 * 60 * 60 * 1000; //the max zoom interval is set to 5 years in milliseconds
export const TIMELINE_MOVEMENT_PERCENTAGE = 0.2;

//10 (the max number of rows displayed) * 30px (each row is of height 30px by default) + 52px (height of time axis)
export const TIMELINE_MAX_HEIGHT = 10 * 30 + 52;

export const WAVE_OR_GROUP_TABS: TabsProps.Tab[] = [
    { id: 'waves', label: constants.WAVES },
    { id: 'groups', label: constants.GROUPS },
];

export const DEFAULT_APP_CAPACITY = 20;
export const DEFAULT_SERVER_CAPACITY = 150;
export const DEFAULT_DB_CAPACITY = 30;
export const MAX_WAVE_CAPACITY = 1000;
export const MAX_COMMENT_LENGTH = 100;

// Input Date should be later than 1970/01/01
export const EARLIEST_WAVE_DATE = new Date(0);

// Input Date should be earlier than 2100/01/01
export const LATEST_WAVE_DATE = new Date(2100, 1, 1);

export enum AddWaveTypes {
    ADD_A_WAVE = 'addAWave',
    AUTO_CREATE_WAVES = 'autoCreateWaves',
};

export const ADD_WAVE_OPTIONS: RadioGroupProps.RadioButtonDefinition[] = [
    { value: AddWaveTypes.ADD_A_WAVE, label: constants.ADD_A_WAVE },
    { value: AddWaveTypes.AUTO_CREATE_WAVES, label: constants.AUTO_CREATE_WAVES },
];

export const START_DAY_OPTIONS: RadioGroupProps.RadioButtonDefinition[] = [
    { value: StartDayOption.START_ON_MONDAY, label: constants.MONDAY },
    { value: StartDayOption.START_ON_ANY_DAY, label: constants.ANY_DAY_OF_WEEK },
];

export enum GroupSortingOptions {
    NAME_ASC = 'name',
    NAME_DESC = '-name',
    RANKING_ASC = 'ranking',
    RANKING_DESC = '-ranking',
};

export const GROUP_SORTING_OPTIONS: SelectProps.Options = [
    { label: constants.NAME_ASCENDING, value: GroupSortingOptions.NAME_ASC },
    { label: constants.NAME_DESCENDING, value: GroupSortingOptions.NAME_DESC },
    { label: constants.RANKING_ASCENDING, value: GroupSortingOptions.RANKING_ASC },
    { label: constants.RANKING_DESCENDING, value: GroupSortingOptions.RANKING_DESC },
];

export enum WaveSortingOptions {
    START_DATE_ASC = 'startDate',
    START_DATE_DESC = '-startDate',
    END_DATE_ASC = 'endDate',
    END_DATE_DESC = '-endDate',
    NAME_ASC = 'name',
    NAME_DESC = '-name',
    OPEN_FIRST = '-closed',
    CLOSED_FIRST = 'closed',
    TOTAL_SERVERS_ASC = 'totalServers',
    TOTAL_SERVERS_DESC = '-totalServers',
    TOTAL_DATABASES_ASC = 'totalDatabases',
    TOTAL_DATABASES_DESC = '-totalDatabases',
};

export const WAVE_SORTING_OPTIONS: SelectProps.Options = [
    { label: constants.START_DATE_ASCENDING, value: WaveSortingOptions.START_DATE_ASC },
    { label: constants.START_DATE_DESCENDING, value: WaveSortingOptions.START_DATE_DESC },
    { label: constants.END_DATE_ASCENDING, value: WaveSortingOptions.END_DATE_ASC },
    { label: constants.END_DATE_DESCENDING, value: WaveSortingOptions.END_DATE_DESC },
    { label: constants.NAME_ASCENDING, value: WaveSortingOptions.NAME_ASC },
    { label: constants.NAME_DESCENDING, value: WaveSortingOptions.NAME_DESC },
    { label: constants.OPEN_FIRST, value: WaveSortingOptions.OPEN_FIRST },
    { label: constants.CLOSED_FIRST, value: WaveSortingOptions.CLOSED_FIRST },
    { label: constants.SERVERS_ASCENDING, value: WaveSortingOptions.TOTAL_SERVERS_ASC },
    { label: constants.SERVERS_DESCENDING, value: WaveSortingOptions.TOTAL_SERVERS_DESC },
    { label: constants.DATABASE_ASCENDING, value: WaveSortingOptions.TOTAL_DATABASES_ASC },
    { label: constants.DATABASE_DESCENDING, value: WaveSortingOptions.TOTAL_DATABASES_DESC },
];

// wave start and end times in UTC
// the 'Z' at the end of the time indicates UTC
// using UTC time to be consistent across timezones
export const WAVE_START_TIME = 'T00:00:00Z';
export const WAVE_END_TIME = 'T23:59:59Z';

export const groupTabColumnDefinitions: ColumnDefinition<ApplicationGroup>[] = [{
    id: 'name',
    header: constants.GROUP_NAME,
    cell: item => item.name,
    type: ColumnTypes.TEXT,
}, {
    id: 'totalApplications',
    header: constants.APPLICATIONS,
    data: item => item.totalApplications || 0,
    cell: item => getCell(formatNumberAsString(item.totalApplications || 0)),
    type: ColumnTypes.NUMBER,
}, {
    id: 'totalServers',
    header: constants.SERVERS,
    data: item => item.totalServers || 0,
    cell: item => getCell(formatNumberAsString(item.totalServers || 0)),
    type: ColumnTypes.NUMBER,
}, {
    id: 'totalDatabases',
    header: constants.DATABASES,
    data: item => item.totalDatabases || 0,
    cell: item => getCell(formatNumberAsString(item.totalDatabases || 0)),
    type: ColumnTypes.NUMBER,
}, {
    id: 'ranking',
    header: constants.RANKING,
    data: item => item.ranking || 0,
    cell: item => getCell(formatNumberAsString(item.ranking || 0)),
    type: ColumnTypes.NUMBER,
}, {
    id: 'migrationWaveName',
    header: constants.WAVE_NAME,
    data: item => item.migrationWaveName || '',
    cell: item => item.migrationWaveName || '',
    type: ColumnTypes.TEXT,
}];
